import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ClassroomGridCard } from '@Components/AbwaabPlus'
import { useGetEducationInfoQuery } from '@Features/Search/SearchApi/SearchApi'
import { NumberFormatter } from '@Utilities/Formatters'
import usePlatformFlags from '@Hooks/usePlatformFlags'

type Props = {
	course: StandaloneCourse
	/** /mine rows: the nested course has no ownership merge — force it here */
	owned?: boolean
	/** /mine rows carry expiry on the enrollment row, not the course */
	expired?: boolean
	className?: string
}

/**
 * The one standalone-course browse card — same rows as the classroom card
 * (title · instructor · type(s) · year(s) with the +x overflow), كورس mode
 * badge, price + اشترك CTA when not owned, مشترك/منتهي when owned. Education
 * labels render from whatever the payload carries: educationTypes directly
 * (catalog), or years mapped to their parent type via the cached
 * education-info dropdown (search/instructor payloads carry years only).
 */
const StandaloneCourseCard: React.FC<Props> = ({ course, owned: ownedProp, expired = false, className }) => {
	const [t] = useTranslation('standalone_course')
	const { t: tEdu } = useTranslation('educational_info')
	const { t: tGen } = useTranslation('generic')
	const navigate = useNavigate()
	const { data: eduRes } = useGetEducationInfoQuery()

	/** education-year id → parent education-type name */
	const yearTypeMap = useMemo(() => {
		const map = new Map<number, string>()
		for (const ty of eduRes?.data ?? []) {
			for (const y of ty.educationYears ?? []) map.set(y.id, ty.name)
		}
		return map
	}, [eduRes?.data])

	const years = course.educationYears ?? []
	const yearLabels = years.map(y => tEdu(`academic_year.${y.name}`, { defaultValue: y.name }))
	const typeNames = course.educationTypes?.length
		? course.educationTypes.map(ty => ty.name)
		: Array.from(new Set(years.map(y => yearTypeMap.get(y.id)).filter(Boolean) as string[]))
	const typeLabels = typeNames.map(name => tEdu(`educational_type.${name}`, { defaultValue: name }))

	const owned = ownedProp ?? (expired || !!course.owned || !!course.enrollment)
	const instructorUser = course.instructor?.user
	const instructorName = instructorUser ? `${instructorUser.first_name ?? ''} ${instructorUser.last_name ?? ''}`.trim() : undefined

	// Bundle-membership pills — titles only on cards (no savings, no fetches);
	// tapping a pill goes to the bundle, not the course.
	const { bundlesEnabled } = usePlatformFlags()
	const chips = bundlesEnabled
		? (course.bundles ?? []).map(b => ({ key: b.id, label: b.title, onClick: () => navigate(`/bundles/${b.label}`) }))
		: undefined

	return (
		<ClassroomGridCard
			className={className}
			title={course.title ?? course.name ?? ''}
			instructorName={instructorName}
			typeLabels={typeLabels}
			yearLabels={yearLabels}
			mode={t('course_mode', { defaultValue: 'كورس' })}
			subjectLabel={course.category?.name}
			photo={course.thumbnail}
			enrolled={owned && !expired}
			enrolledLabel={t('owned_chip', { defaultValue: 'مشترك' })}
			expired={expired}
			expiredLabel={t('expired_chip', { defaultValue: 'منتهي' })}
			priceText={
				owned ? undefined : Number(course.price) === 0 ? tGen('free', { defaultValue: 'مجاني' }) : `${NumberFormatter(course.price)} ${tGen('egp')}`
			}
			ctaLabel={t('subscribe_cta', { defaultValue: 'اشترك' })}
			chips={chips}
			onClick={() => navigate(`/courses/${course.label}`)}
		/>
	)
}

export default StandaloneCourseCard
